// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Button, Divider, MenuItem, TextField } from '@mui/material';

// shared components
import AzureStyleDateRangeSelector from '@/components/ui/datetime/AzureStyleDateRangeSelector';

// relative
import {
  LIVE_RANGE_OPTIONS,
  type LiveRangeMinutes,
} from '../hooks/useLiveModeRange';

import type { DateTime } from 'luxon';

interface DataViewerDateRangeSelectorProps {
  from: DateTime | null;
  to: DateTime | null;
  onRangeChange: (range: { from: DateTime; to: DateTime }) => void;
  liveMode: boolean;
  liveRangeMinutes: LiveRangeMinutes;
  onLiveRangeChange: (minutes: LiveRangeMinutes) => void;
  onToggleLive: () => void;
  containerDataTour?: string;
  rangeButtonDataTour?: string;
  liveButtonDataTour?: string;
}

const DataViewerDateRangeSelector = ({
  from,
  to,
  onRangeChange,
  liveMode,
  liveRangeMinutes,
  onLiveRangeChange,
  onToggleLive,
  containerDataTour,
  rangeButtonDataTour,
  liveButtonDataTour,
}: DataViewerDateRangeSelectorProps) => {
  const { t } = useTranslation('view-data');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
      }}
      minWidth={{ xs: '100%', md: 'auto' }}
      data-tour={containerDataTour}
    >
      {liveMode ? (
        <TextField
          select
          size="small"
          label={t('live_range.label')}
          value={String(liveRangeMinutes)}
          onChange={(event) =>
            onLiveRangeChange(Number(event.target.value) as LiveRangeMinutes)
          }
          sx={{ minWidth: { xs: '100%', md: 140 } }}
        >
          {LIVE_RANGE_OPTIONS.map((minutes) => (
            <MenuItem key={minutes} value={String(minutes)}>
              {t(`live_range.options.${minutes}`)}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <>
          <AzureStyleDateRangeSelector
            from={from}
            to={to}
            onRangeChange={onRangeChange}
            rangeButtonDataTour={rangeButtonDataTour}
          />
          <Divider orientation="vertical" flexItem />
        </>
      )}

      <Button
        variant={'contained'}
        color={liveMode ? 'primary' : 'inherit'}
        size="small"
        onClick={onToggleLive}
        data-tour={liveButtonDataTour}
        startIcon={
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: liveMode ? 'success.main' : 'grey.500',
              boxShadow: liveMode
                ? '0 0 0 3px rgba(46, 125, 50, 0.18)'
                : 'none',
            }}
          />
        }
        sx={{
          whiteSpace: 'nowrap',
          height: 37,
          alignSelf: 'flex-end',
          flexShrink: 0,
        }}
      >
        {t('live')}
      </Button>
    </Box>
  );
};

export default DataViewerDateRangeSelector;
